import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { fetchAllOpportunities, completeAction } from '@/features/opportunity-center/api/opportunity-center.api'
import ToastService from '@/common/services/toast.service'
import type { OpportunityCenterResponse, ActionResponse } from '@/features/opportunity-center/types/opportunity-center.types'

export const useOpportunityStore = defineStore('opportunityStore', () => {
  // State
  const opportunities = ref<OpportunityCenterResponse | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const opportunityNotification = ref(0)

  const isOpportunityAvailable = computed(() => opportunityNotification.value > 0)

  // Fetch all opportunities
  const fetchOpportunitiesData = async () => {
    isLoading.value = true
    error.value = null

    try {
      const response = await fetchAllOpportunities()
      opportunities.value = response
      updateOpportunityNotification()
    } catch {
      // temporary error response
      error.value = 'An error occurred while fetching opportunities'
      ToastService.error(error.value)
    } finally {
      isLoading.value = false
    }
  }

  // Complete an action
  const completeOpportunityAction = async (action: ActionResponse) => {
    try {
      await completeAction(action)
      await fetchOpportunitiesData()
    } catch {
      error.value = `Failed to complete action ${action.codename}`
      ToastService.error(error.value)
    }
  }

  // Update the opportunity notification count
  const updateOpportunityNotification = () => {
    if (opportunities.value) {
      opportunityNotification.value = opportunities.value.topics.reduce((total, topic) => {
        const createdCount = topic.opportunities.filter(opportunity => opportunity.state === 'created').length
        return total + createdCount
      }, 0)
    }
  }

  return {
    // State
    opportunities,
    isLoading,
    error,
    opportunityNotification,
    isOpportunityAvailable,

    // Actions
    fetchOpportunitiesData,
    completeOpportunityAction,
    updateOpportunityNotification,
  }
})
